import { useState } from 'react';
import ServiceModal from './ServiceModal';

const services = [
  {
    icon: 'ri-truck-line',
    title: 'Refrigerated Transport',
    description:
      'Specialized vehicles with temperature control systems for safe delivery of perishable food products',
    features: [
      'Temperature range from -25°C to +25°C',
      'Real-time GPS monitoring',
      'Certified drivers',
      '24/7 delivery across Canada',
      'Automated temperature logging every 15 minutes',
      'Insulated packaging solutions',
      'Emergency response protocol within 30 minutes',
    ],
    gradient: 'from-[#DC2626] to-[#F97316]',
    longDescription:
      'Our refrigerated transport fleet operates around the clock across all Canadian provinces. Every vehicle is equipped with state-of-the-art temperature control units, real-time telemetry, and automated alerts to ensure your perishable cargo arrives in perfect condition. From fresh produce and dairy to frozen goods and pharmaceuticals, our drivers are trained and certified to handle every category of temperature-sensitive freight.',
    highlights: [
      { icon: 'ri-roadster-line', label: 'Fleet Size', value: '120+ Refrigerated Units' },
      { icon: 'ri-time-line', label: 'Availability', value: '24/7 Year-Round' },
      { icon: 'ri-map-pin-line', label: 'Coverage', value: 'All Canadian Provinces' },
      { icon: 'ri-shield-check-line', label: 'Compliance', value: 'CFIA & Transport Canada' },
    ],
  },
  {
    icon: 'ri-building-line',
    title: 'Cold Storage Warehousing',
    description:
      'State-of-the-art cold storage facilities with multi-zone temperature control and advanced security systems',
    features: [
      'Over 50,000 sq.m of storage space',
      'Separate temperature zones',
      'WMS inventory management system',
      'HACCP compliance certified',
      'Blast freezing capability',
      '24/7 security surveillance',
      'Fire suppression and emergency power backup',
    ],
    gradient: 'from-[#DC2626] to-[#F97316]',
    longDescription:
      'McALLISTER DELIGHT\'s cold storage network provides flexible, scalable warehousing solutions tailored for the food industry. Our facilities maintain multiple temperature zones from ambient (+18°C) down to deep-freeze (-30°C), allowing us to store diverse product ranges under one roof. Advanced WMS software gives clients full inventory visibility in real time, with detailed stock movement history and automated replenishment alerts.',
    highlights: [
      { icon: 'ri-layout-grid-line', label: 'Total Space', value: '50,000+ sq.m' },
      { icon: 'ri-temp-cold-line', label: 'Temp Range', value: '-30°C to +18°C' },
      { icon: 'ri-bar-chart-line', label: 'Accuracy', value: '99.8% Inventory Accuracy' },
      { icon: 'ri-award-line', label: 'Certification', value: 'HACCP & SQF Level 2' },
    ],
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Canada-Wide Distribution',
    description:
      'Comprehensive product distribution solutions with optimized routes and delivery schedules',
    features: [
      'Coverage across all Canadian provinces',
      'Cross-docking services',
      'Freight consolidation',
      'Flexible delivery schedules',
      'Last-mile urban delivery solutions',
      'EDI integration with retail partners',
      'Dedicated account manager',
    ],
    gradient: 'from-[#DC2626] to-[#F97316]',
    longDescription:
      'Our distribution network covers every corner of Canada, from major metropolitan hubs to remote northern communities. We combine route optimization technology with local expertise to reduce transit times and lower costs. Cross-docking and freight consolidation services allow businesses of all sizes to benefit from shared network efficiencies without sacrificing reliability or cold-chain integrity. A dedicated account manager is assigned to every client.',
    highlights: [
      { icon: 'ri-global-line', label: 'Network', value: '13 Provinces & Territories' },
      { icon: 'ri-exchange-line', label: 'Cross-Dock Points', value: '18 Strategically Located' },
      { icon: 'ri-user-star-line', label: 'Clients Served', value: '400+ Active Partners' },
      { icon: 'ri-speed-line', label: 'On-Time Rate', value: '98.4% On-Time Delivery' },
    ],
  },
];

export default function Services() {
  const [selectedService, setSelectedService] = useState<typeof services[0] | null>(null);

  return (
    <>
      <section id="services" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-12">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-[#DC2626] text-xs md:text-sm font-bold uppercase tracking-wider mb-3 md:mb-4">
              OUR SERVICES
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A365D] mb-3 md:mb-4">
              Comprehensive Logistics Solutions
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
              We provide a full range of services for the food industry with guaranteed
              quality and safety
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 md:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 md:p-10 border border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div
                  className={`w-14 h-14 md:w-20 md:h-20 rounded-full bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-4 md:mb-6`}
                >
                  <i className={`${service.icon} text-2xl md:text-3xl text-white`}></i>
                </div>

                <h3 className="text-xl md:text-2xl font-bold text-[#1A365D] mb-3 md:mb-4">
                  {service.title}
                </h3>

                <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4 md:mb-6">
                  {service.description}
                </p>

                <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                  {service.features.slice(0, 4).map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-start space-x-2 text-xs md:text-sm text-gray-700"
                    >
                      <i className="ri-checkbox-circle-fill text-[#DC2626] text-base md:text-lg flex-shrink-0 mt-0.5"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setSelectedService(service)}
                  className="text-[#DC2626] font-semibold text-sm hover:text-[#B91C1C] transition-colors flex items-center space-x-1 whitespace-nowrap cursor-pointer"
                >
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServiceModal
        service={selectedService}
        onClose={() => setSelectedService(null)}
      />
    </>
  );
}
